import { Box, Typography } from "@mui/material";
import { OwnershipEntry } from "../../types/Ownership";
import { getValueDisplay } from "../../data/utilities";
import { DELTA_PRIMARY, TEXT_SECONDARY, BORDER_GREY } from "../colors";

const MAX_TIER_NODES = 4;
const NODE_HEIGHT = 56;
const HUB_HEIGHT = 56;
const TRUNK_GAP = 56; // vertical space between a tier and the hub, for the connector trunk
const GAP = 24;
const SAFE_WIDTH = 600; // viewBox is 680 wide, 40px margin each side
const MAX_BOX_WIDTH = 300;

// Evenly lays out n boxes within the safe width, capping how wide any one box
// gets so a single node doesn't stretch edge to edge, and centering the row.
const layoutRow = (n: number) => {
  const rawWidth = (SAFE_WIDTH - (n - 1) * GAP) / n;
  const boxWidth = Math.min(rawWidth, MAX_BOX_WIDTH);
  const totalWidth = n * boxWidth + (n - 1) * GAP;
  const startX = 40 + (SAFE_WIDTH - totalWidth) / 2;
  return { boxWidth, xs: Array.from({ length: n }, (_, i) => startX + i * (boxWidth + GAP)) };
};

// Conservative per-character width estimate (accounting for internal
// padding) - company names run long, and an optimistic estimate here is
// exactly what let text spill past the box edge in practice.
const truncate = (text: string, boxWidth: number, charWidth: number) => {
  const maxChars = Math.floor((boxWidth - 16) / charWidth);
  return text.length > maxChars ? text.slice(0, Math.max(1, maxChars - 1)) + "…" : text;
};

// One level each direction (owners above, subsidiaries below) - the API
// doesn't give owners-of-owners or subsidiaries-of-subsidiaries without
// recursive fetching of unknown depth, and most companies only have a
// handful of entries at either level anyway.
export const GroupStructureChart = (props: {
  companyName: string;
  owners: OwnershipEntry[];
  subsidiaries: OwnershipEntry[];
}) => {
  const { companyName, owners, subsidiaries } = props;

  const ownersShown = owners.slice(0, MAX_TIER_NODES);
  const subsidiariesShown = subsidiaries.slice(0, MAX_TIER_NODES);
  const ownersOverflow = owners.length - ownersShown.length;
  const subsidiariesOverflow = subsidiaries.length - subsidiariesShown.length;

  if (ownersShown.length === 0 && subsidiariesShown.length === 0) {
    return null;
  }

  const ownersY = 20;
  const hubY = ownersShown.length > 0 ? ownersY + NODE_HEIGHT + TRUNK_GAP : 20;
  const subsidiariesY = hubY + HUB_HEIGHT + TRUNK_GAP;
  const viewBoxHeight = (subsidiariesShown.length > 0 ? subsidiariesY + NODE_HEIGHT : hubY + HUB_HEIGHT) + 20;

  const ownersLayout = layoutRow(ownersShown.length);
  const subsidiariesLayout = layoutRow(subsidiariesShown.length);
  const hubLayout = layoutRow(1);
  const hubX = hubLayout.xs[0];
  const hubWidth = hubLayout.boxWidth;
  const hubMidX = hubX + hubWidth / 2;

  const renderNode = (entry: OwnershipEntry, x: number, width: number, y: number, key: string) => {
    const label = entry.natures_of_control?.[0] ? getValueDisplay(entry.natures_of_control[0]) : "";
    return (
      <g key={key}>
        <rect x={x} y={y} width={width} height={NODE_HEIGHT} rx="8" fill="white" stroke={BORDER_GREY} strokeWidth="1" />
        <text x={x + width / 2} y={y + 22} textAnchor="middle" fontSize="13" fontWeight={500} fill={DELTA_PRIMARY}>
          {truncate(entry.company_name, width, 7.5)}
        </text>
        {label && (
          <text x={x + width / 2} y={y + 40} textAnchor="middle" fontSize="11" fill={TEXT_SECONDARY}>
            {truncate(label, width, 6.5)}
          </text>
        )}
      </g>
    );
  };

  // Trunk-and-branch connector: hub connects to a horizontal bar spanning the
  // tier's node centers, then a vertical drop into each node. `tierIsAboveHub`
  // says whether this tier sits above the hub (owners) or below it
  // (subsidiaries) - the edges and bar need to face each other across the gap.
  const renderConnectors = (tierXs: number[], tierWidth: number, tierY: number, tierIsAboveHub: boolean) => {
    if (tierXs.length === 0) return null;
    const centers = tierXs.map((x) => x + tierWidth / 2);
    const tierEdgeY = tierIsAboveHub ? tierY + NODE_HEIGHT : tierY;
    const hubEdgeY = tierIsAboveHub ? hubY : hubY + HUB_HEIGHT;
    const barY = (tierEdgeY + hubEdgeY) / 2;

    return (
      <g stroke={BORDER_GREY} strokeWidth="1" fill="none">
        <path d={`M${hubMidX} ${hubEdgeY} L${hubMidX} ${barY}`} />
        {centers.length > 1 && (
          <path d={`M${Math.min(...centers)} ${barY} L${Math.max(...centers)} ${barY}`} />
        )}
        {centers.map((cx, i) => (
          <path key={i} d={`M${cx} ${barY} L${cx} ${tierEdgeY}`} />
        ))}
      </g>
    );
  };

  return (
    <Box style={{ padding: 10 }}>
      <svg width="100%" viewBox={`0 0 680 ${viewBoxHeight}`} role="img">
        <title>Group structure</title>
        <desc>
          Companies that own this one shown above, companies this one owns shown below.
        </desc>

        {ownersShown.length > 0 && renderConnectors(ownersLayout.xs, ownersLayout.boxWidth, ownersY, true)}
        {subsidiariesShown.length > 0 &&
          renderConnectors(subsidiariesLayout.xs, subsidiariesLayout.boxWidth, subsidiariesY, false)}

        <g>
          <rect x={hubX} y={hubY} width={hubWidth} height={HUB_HEIGHT} rx="8" fill={DELTA_PRIMARY} />
          <text x={hubMidX} y={hubY + HUB_HEIGHT / 2} textAnchor="middle" dominantBaseline="central" fill="white" fontSize="13" fontWeight={500}>
            {truncate(companyName, hubWidth, 7.5)}
          </text>
        </g>

        {ownersShown.map((entry, i) =>
          renderNode(entry, ownersLayout.xs[i], ownersLayout.boxWidth, ownersY, `owner-${entry.company_number || i}`)
        )}
        {subsidiariesShown.map((entry, i) =>
          renderNode(entry, subsidiariesLayout.xs[i], subsidiariesLayout.boxWidth, subsidiariesY, `sub-${entry.company_number || i}`)
        )}
      </svg>
      {(ownersOverflow > 0 || subsidiariesOverflow > 0) && (
        <Typography variant="body2" style={{ color: TEXT_SECONDARY, textAlign: "center", marginTop: -8 }}>
          {ownersOverflow > 0 && `+${ownersOverflow} more owner${ownersOverflow > 1 ? "s" : ""}`}
          {ownersOverflow > 0 && subsidiariesOverflow > 0 && " · "}
          {subsidiariesOverflow > 0 && `+${subsidiariesOverflow} more subsidiar${subsidiariesOverflow > 1 ? "ies" : "y"}`}
          {" (see tables below)"}
        </Typography>
      )}
    </Box>
  );
};

export default GroupStructureChart;
